import React, { useState } from 'react';
import axios from 'axios';
import { Button, Box, Typography, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminRGCustomerExport() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      let all = [];
      let page = 1;
      let fetched;
      const pageSize = 100;
      do {
        // eslint-disable-next-line no-console
        console.log(`Fetching page ${page} with pageSize ${pageSize}`);
        const res = await axios.get(`${API_URL}/api/realgreen/Customer/Search?CustomerStatusMinValue=9&CustomerStatusMaxValue=9&page=${page}&pageSize=${pageSize}`);
        const data = res.data || [];
        // eslint-disable-next-line no-console
        console.log(`Fetched ${data.length} customers on page ${page}`);
        fetched = Array.isArray(data) ? data.length : 0;
        if (fetched > 0) all = all.concat(data);
        page++;
      } while (fetched === pageSize);
      setCustomers(all);
      setSuccess(`Fetched ${all.length} customers.`);
    } catch (e) {
      setError('Failed to fetch customers from RG.');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!customers || customers.length === 0) {
      setError('No customer data to export.');
      return;
    }
    // Get all unique keys from all customer objects
    const allKeys = Array.from(new Set(customers.flatMap(obj => Object.keys(obj))));
    const csvRows = [];
    csvRows.push(allKeys.join(','));
    customers.forEach(item => {
      const row = allKeys.map(key => {
        let val = item[key];
        if (val === undefined || val === null) val = '';
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          val = '"' + val.replace(/"/g, '""') + '"';
        }
        return val;
      });
      csvRows.push(row.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rg_customers.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess('CSV export successful!');
  };

  return (
    <Box sx={{ mt: 4, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Export RG Customers (Status Min/Max contains "9")
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Button
        variant="contained"
        color="primary"
        onClick={fetchCustomers}
        disabled={loading}
        sx={{ mr: 2, mb: 2 }}
      >
        {loading ? <CircularProgress size={22} /> : 'Search & Fetch Customers'}
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        onClick={exportToCSV}
        disabled={loading || customers.length === 0}
        sx={{ mb: 2 }}
      >
        Export as CSV
      </Button>
      {customers.length > 0 && (
        <Box sx={{ maxHeight: 400, overflow: 'auto', mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {Object.keys(customers[0]).map(key => (
                  <TableCell key={key} sx={{ fontWeight: 700 }}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.slice(0, 20).map((row, idx) => (
                <TableRow key={idx}>
                  {Object.keys(customers[0]).map(key => {
                    const val = row[key];
                    return (
                      <TableCell key={key}>
                        {typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Showing first 20 results. All data will be included in CSV export.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
